/**
 * C26 链路总览 fixture · mock 模式(?mock=1)供 e2e/离线预览。
 * 真数据源=后端 /loop-map(规则节点直引 RULE_REGISTRY);此处为代表性子集,
 * 关键断言字段(G8 serves/mechanism)与注册表口径一致。
 */

import type { LoopMapNode, LoopMapResponse } from "@/lib/api/loop-lab";

import { registerFixture } from "./registry";

const N = (n: LoopMapNode): [string, LoopMapNode] => [n.id, n];

const NODES: Record<string, LoopMapNode> = Object.fromEntries([
	N({
		id: "USER",
		name: "用户消息",
		kind: "flow",
		definition: "一轮对话的输入起点",
		actual: "验证台试跑/评测机器顾客发来的当轮消息",
		where: "session.handle_session_turn(user_msg)",
	}),
	N({
		id: "N1",
		name: "N1 意图契约",
		kind: "llm",
		definition: "单次 LLM 调用,把用户话变成结构化契约(intent + entities + 接续 resume)",
		actual: "15 意图扁平抽取;多轮接续已折叠进 N1",
		where: "nodes.n1_intent + prompts/n1_intent_*.txt",
		refs: "C2 S-A",
	}),
	N({
		id: "N2",
		name: "N2 主 Agent 决策",
		kind: "llm",
		definition: "循环内的决策大脑",
		actual: "每轮从 allowed 圈内选「调哪个 skill / 反问 / 收尾」;只决策不组参",
		where: "nodes.n2_decide + llm_loop.handle_turn_llm",
	}),
	N({
		id: "B2.5",
		name: "组参承载",
		kind: "rule",
		definition: "参数确定性,N2 只决策不组参(分工铁律)",
		actual: "契约 entities 重命名+结构组装为 skill 入参;LLM 不手拼参数",
		where: "rules.assemble(14 个 skill 分支)",
		stage: "N2 决策后·每次调 skill 前",
		when: "N2 选定 skill(每轮必经)",
		output_to: "params→G14→G5/G8→G1→skill 入参",
	}),
	N({
		id: "G8",
		name: "写前归属校验",
		kind: "rule",
		definition: "越权写零通过(SIM-overreach-auth 由此直接转化)",
		actual: "orderId 归属 ≠ 当前客户 → cannot 拦截;只拦「确实存在但属别人」的越权写",
		where: "rules.order_owned + llm_loop(写 skill 执行前)",
		stage: "写 skill 执行前",
		when: "params.orderId 存在且归属 ≠ 当前客户",
		output_to: "stop=cannot 终止本轮→N3 拒绝话术;不进 skill",
	}),
	N({
		id: "SKILL",
		name: "Skill 执行层",
		kind: "tool",
		definition: "14 个 Shopify 语义 skill 的执行",
		actual: "fixtures 罐头按真实 Shopify 语义返回,金额=系统口径",
		where: "fixtures.call_skill_fixture",
	}),
	N({
		id: "G4",
		name: "判停",
		kind: "rule",
		definition: "何时停何时续不靠模型自觉",
		actual: "skill status → multi/empty/cannot/handoff/blocked/confirm_gate/done 确定性映射",
		where: "rules.judge_stop",
		stage: "skill 返回后",
		when: "每次 skill 调用返回",
		output_to: "stop 标签→分流",
	}),
	N({
		id: "N3",
		name: "N3 话术生成",
		kind: "llm",
		definition: "把执行结果变成给用户的话",
		actual: "忠实红线:卡片只出骨架,数据由 O1 回填",
		where: "nodes.n3_generate",
	}),
	N({
		id: "O1",
		name: "块回填",
		kind: "rule",
		definition: "N3 不造事实(忠实红线的承载面)",
		actual: "N3 出块骨架,商品卡/券数据由系统从 skill 真实返回回填",
		where: "cards.enrich_blocks",
		stage: "N3 输出后",
		when: "N3 blocks 含 product/coupon 骨架",
		output_to: "最终回复 blocks",
	}),
	N({
		id: "REPLY",
		name: "回复输出",
		kind: "flow",
		definition: "本轮最终输出",
		actual: "reply 文本+blocks",
		where: "llm_loop._result",
	}),
	N({
		id: "S5",
		name: "挂起态机",
		kind: "rule",
		definition: "反问与确认门的跨轮闭环",
		actual: "clarify/confirm_gate → pending,下轮按 resume 语义续跑",
		where: "session._pending_from",
		stage: "跨轮(每轮收尾)",
		when: "本轮 stop=clarify/confirm_gate",
		output_to: "pending→下轮 N1 输入",
	}),
	N({
		id: "V1",
		name: "last 数据摘要",
		kind: "rule",
		definition: "兑现 N2 提示词「从 last 取数」的承诺",
		actual: "skill 返回的状态+有界数据摘要注入 N2 的 last 视图",
		where: "nodes._result_summary",
		stage: "N2 输入面(每轮)",
		when: "上轮有 skill 返回",
		output_to: "N2 last 视图",
	}),
]);

const GRAPH: LoopMapResponse["graph"] = {
	session_lane: ["S5"],
	main_lane: ["USER", "N1", "LOOP", "N3", "O1", "REPLY"],
	input_lane: ["V1"],
	loop: {
		budget: "N2-BUDGET",
		steps: ["N2", "B2.5", "G8", "SKILL", "G4"],
		n2_guards: [],
		b25_resolve: [],
		skill_gates: [],
		stop_outs: [],
	},
	n1_fallback: [],
	n1_outputs: [],
	n3_inputs: [],
	config: [],
};

export function registerLoopOverviewFixtures(): void {
	registerFixture({
		pathname: "/admin/v1/loop-lab/loop-map",
		methods: ["GET"],
		resolve: () => ({ data: { graph: GRAPH, nodes: NODES } satisfies LoopMapResponse }),
	});
}
